import React, { useCallback, useState } from "react";
import { useTheme } from "@mui/material";
import Grid from "@mui/material/Grid2";
import DashboardReport from "components/reports/templates/DashboardReport";
import ReportBarChart from "components/reports/charts/ReportBarChart";
import ReportLineChart from "components/reports/charts/ReportLineChart";
import ReportPieChart from "components/reports/charts/ReportPieChart";
import DrillDownDrawer from "components/reports/shared/DrillDownDrawer";
import { getChartColors } from "theme";
import api from "services/api";

const formatNumber = (value) => (value || 0).toLocaleString();

const formatPercent = (value) => `${(value || 0).toFixed(1)}%`;

const formatCurrency = (value) =>
  `$${(value || 0).toLocaleString(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const formatDate = (value) => {
  if (!value) return "-";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "-";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const createEmptyDrillDown = () => ({
  open: false,
  title: "",
  subtitle: "",
  description: "",
  data: [],
  columns: [],
  summary: null,
});

const useServiceOpsDrillDown = () => {
  const [drillDown, setDrillDown] = useState(createEmptyDrillDown());

  const closeDrillDown = useCallback(() => {
    setDrillDown(createEmptyDrillDown());
  }, []);

  const openDrillDown = useCallback(
    ({ title, subtitle, description, data, columns, summary }) => {
      setDrillDown({
        open: true,
        title: title || "Details",
        subtitle: subtitle || "",
        description: description || "",
        data: data || [],
        columns: columns || [],
        summary: summary || null,
      });
    },
    [],
  );

  return { drillDown, closeDrillDown, openDrillDown };
};

const VOLUME_COLUMNS = [
  { field: "monthLabel", headerName: "Month", width: 130 },
  { field: "total", headerName: "Total", width: 110 },
  { field: "translation", headerName: "Translation", width: 130 },
  { field: "interpretation", headerName: "Interpretation", width: 140 },
  { field: "other", headerName: "Other", width: 110 },
];

const TURNAROUND_COLUMNS = [
  { field: "clientName", headerName: "Client", width: 220 },
  {
    field: "requestDate",
    headerName: "Request Date",
    width: 130,
    renderCell: (row) => formatDate(row.requestDate),
  },
  {
    field: "deliveryDate",
    headerName: "Delivery Date",
    width: 130,
    renderCell: (row) => formatDate(row.deliveryDate),
  },
  {
    field: "turnaroundDays",
    headerName: "Turnaround",
    width: 110,
    renderCell: (row) => `${(row.turnaroundDays || 0).toFixed(1)} days`,
  },
  {
    field: "onTime",
    headerName: "On Time",
    width: 110,
    renderCell: (row) => (row.onTime ? "Yes" : "No"),
  },
];

const SERVICE_TYPE_COLUMNS = [
  { field: "serviceType", headerName: "Service Type", width: 260 },
  { field: "category", headerName: "Category", width: 140 },
  { field: "servicesCount", headerName: "Jobs", width: 90 },
  {
    field: "revenue",
    headerName: "Revenue",
    width: 130,
    renderCell: (row) => formatCurrency(row.revenue),
  },
  {
    field: "profitMargin",
    headerName: "Margin",
    width: 110,
    renderCell: (row) => `${(row.profitMargin || 0).toFixed(1)}%`,
  },
];

const RELIABILITY_COLUMNS = [
  { field: "clientName", headerName: "Client", width: 230 },
  { field: "servicesCount", headerName: "Services", width: 90 },
  {
    field: "completionRate",
    headerName: "Completion",
    width: 110,
    renderCell: (row) => `${(row.completionRate || 0).toFixed(1)}%`,
  },
  {
    field: "onTimeRate",
    headerName: "On Time",
    width: 100,
    renderCell: (row) => `${(row.onTimeRate || 0).toFixed(1)}%`,
  },
  {
    field: "cancellationRate",
    headerName: "Cancellation",
    width: 120,
    renderCell: (row) => `${(row.cancellationRate || 0).toFixed(1)}%`,
  },
  {
    field: "avgLateDays",
    headerName: "Avg Late Days",
    width: 120,
    renderCell: (row) => `${(row.avgLateDays || 0).toFixed(1)}d`,
  },
  { field: "riskLevel", headerName: "Risk", width: 100 },
];

const toMonthLabel = (monthKey) => {
  if (!monthKey || typeof monthKey !== "string" || !monthKey.includes("-")) {
    return monthKey || "Unknown";
  }

  const [year, month] = monthKey.split("-");
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return monthKey;

  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
  });
};

const parseDate = (value) => {
  if (!value) return null;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
};

const normalizeServiceOpsData = ({ volume, turnaround, serviceMix, sla }) => {
  const monthlyVolume = (volume?.tableData || [])
    .map((row) => ({
      ...row,
      monthLabel: toMonthLabel(row.month),
      total: Number(row.total || 0),
      translation: Number(row.translation || 0),
      interpretation: Number(row.interpretation || 0),
      other: Number(row.other || 0),
    }))
    .sort((a, b) => a.month.localeCompare(b.month));

  const turnaroundRows = (turnaround?.tableData || []).map((row) => {
    const requestDate = parseDate(row.requestDate);
    const monthKey = requestDate
      ? `${requestDate.getFullYear()}-${String(requestDate.getMonth() + 1).padStart(2, "0")}`
      : "Unknown";

    return {
      ...row,
      turnaroundDays: Number(row.turnaroundDays || 0),
      monthKey,
      monthLabel: toMonthLabel(monthKey),
    };
  });

  const monthlyTurnaroundMap = turnaroundRows.reduce((acc, row) => {
    const key = row.monthKey || "Unknown";

    if (!acc[key]) {
      acc[key] = {
        month: key,
        monthLabel: toMonthLabel(key),
        servicesCount: 0,
        onTimeCount: 0,
        totalTurnaroundDays: 0,
      };
    }

    acc[key].servicesCount += 1;
    acc[key].onTimeCount += row.onTime ? 1 : 0;
    acc[key].totalTurnaroundDays += row.turnaroundDays;

    return acc;
  }, {});

  const monthlyTurnaround = Object.values(monthlyTurnaroundMap)
    .map((row) => ({
      ...row,
      onTimeRate:
        row.servicesCount > 0
          ? (row.onTimeCount / row.servicesCount) * 100
          : 0,
      avgTurnaround:
        row.servicesCount > 0
          ? row.totalTurnaroundDays / row.servicesCount
          : 0,
    }))
    .sort((a, b) => a.month.localeCompare(b.month));

  const categoryTotals = monthlyVolume.reduce(
    (acc, row) => {
      acc.total += row.total;
      acc.translation += row.translation;
      acc.interpretation += row.interpretation;
      acc.other += row.other;
      return acc;
    },
    { total: 0, translation: 0, interpretation: 0, other: 0 },
  );

  const servicesByType = (serviceMix?.tableData || []).map((row) => ({
    ...row,
    servicesCount: Number(row.servicesCount || 0),
    revenue: Number(row.revenue || 0),
    profitMargin: Number(row.profitMargin || 0),
  }));

  const interpretationTypes = servicesByType.filter(
    (row) => row.category === "Interpretation",
  );

  const deliveryModeMix = interpretationTypes.reduce(
    (acc, row) => {
      const typeName = (row.serviceType || "").toLowerCase();

      if (
        typeName.includes("virtual") ||
        typeName.includes("phone") ||
        typeName.includes("opi") ||
        typeName.includes("on-demand")
      ) {
        acc.remote += row.servicesCount;
      } else if (typeName.includes("in-person")) {
        acc.onSite += row.servicesCount;
      } else {
        acc.hybridOrUnknown += row.servicesCount;
      }

      return acc;
    },
    { remote: 0, onSite: 0, hybridOrUnknown: 0 },
  );

  const slaSummary = sla?.summary || {};
  const reliabilityRows = (sla?.clientRows || []).map((row) => ({
    ...row,
    servicesCount: Number(row.servicesCount || 0),
    cancellationRate: Number(row.cancellationRate || 0),
    onTimeRate: Number(row.onTimeRate || 0),
    completionRate: Number(row.completionRate || 0),
    avgLateDays: Number(row.avgLateDays || 0),
  }));

  const avgMonthlyVolume =
    monthlyVolume.length > 0 ? categoryTotals.total / monthlyVolume.length : 0;

  const longTurnaroundCount = turnaroundRows.filter(
    (row) => row.turnaroundDays > 5,
  ).length;

  const fastTurnaroundCount = turnaroundRows.filter(
    (row) => row.turnaroundDays <= 2,
  ).length;

  const lateTurnaroundCount = turnaroundRows.filter((row) => !row.onTime).length;

  return {
    monthlyVolume,
    monthlyTurnaround,
    turnaroundRows,
    servicesByType,
    reliabilityRows,
    categoryTotals,
    deliveryModeMix,
    avgMonthlyVolume,
    longTurnaroundCount,
    fastTurnaroundCount,
    lateTurnaroundCount,
    summary: {
      totalServices: Number(volume?.totalServices || categoryTotals.total || 0),
      avgTurnaround: Number(turnaround?.avgTurnaround || 0),
      onTimeRate: Number(turnaround?.onTimeRate || 0),
      completionRate: Number(slaSummary.completionRate || 0),
      cancellationRate: Number(slaSummary.cancellationRate || 0),
      lateRate: Number(slaSummary.lateRate || 0),
      avgLateDays: Number(slaSummary.avgLateDays || 0),
    },
  };
};

const getServiceOpsData = async (filters) => {
  const [volumeRes, turnaroundRes, serviceMixRes, slaRes] = await Promise.all([
    api.get("/reports/service/volume", { params: filters }),
    api.get("/reports/service/turnaround", { params: filters }),
    api.get("/reports/financial/revenue-by-service", { params: filters }),
    api.get("/reports/client/sla", { params: filters }),
  ]);

  return {
    data: normalizeServiceOpsData({
      volume: volumeRes.data?.data,
      turnaround: turnaroundRes.data?.data,
      serviceMix: serviceMixRes.data?.data,
      sla: slaRes.data?.data,
    }),
  };
};

export const ServiceOperationsDashboard = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const { drillDown, closeDrillDown, openDrillDown } = useServiceOpsDrillDown();

  const openVolumeDrillDown = useCallback(
    (data) => {
      openDrillDown({
        title: "Monthly Service Volume Breakdown",
        subtitle: `${formatNumber(data.summary.totalServices)} services across ${data.monthlyVolume.length} months`,
        description:
          "Use this table to inspect month-level demand and service-category mix. Look for spikes where total volume rises faster than interpretation/translation staffing capacity.",
        data: data.monthlyVolume,
        columns: VOLUME_COLUMNS,
        summary: {
          metrics: [
            { label: "Total Services", value: formatNumber(data.summary.totalServices) },
            { label: "Translation", value: formatNumber(data.categoryTotals.translation) },
            { label: "Interpretation", value: formatNumber(data.categoryTotals.interpretation) },
            { label: "Other", value: formatNumber(data.categoryTotals.other) },
          ],
        },
      });
    },
    [openDrillDown],
  );

  const dataFetchFn = useCallback((activeFilters) => getServiceOpsData(activeFilters), []);

  const metricsConfig = useCallback((data) => {
    const total = data.summary.totalServices;
    const translationShare = total > 0 ? (data.categoryTotals.translation / total) * 100 : 0;
    const interpretationShare = total > 0 ? (data.categoryTotals.interpretation / total) * 100 : 0;

    return [
      {
        title: "Total Services",
        value: formatNumber(total),
        subtitle: `${data.monthlyVolume.length} months in period`,
        tooltipTitle: "Total Services",
        tooltipContent: `Total Services = count of all filtered service records in the selected period.

Why this matters:
• This is your baseline demand load.
• Every other operational KPI (on-time, completion, cancellations, staffing stress) should be interpreted against this denominator.

How to diagnose with this metric:
• Volume rising while completion/on-time falls usually means capacity pressure.
• Volume flat but cancellations rise points to process quality, not demand growth.
• Month-level spikes should be matched with staffing plans and lead-time controls.

Click this card to open the full monthly breakdown panel.`,
        onClick: () => openVolumeDrillDown(data),
      },
      {
        title: "Avg Monthly Volume",
        value: formatNumber(Math.round(data.avgMonthlyVolume)),
        subtitle: "Average services per month",
        tooltipTitle: "Average Monthly Volume",
        tooltipContent: `Average Monthly Volume = Total Services / Number of Months in scope.

      Use this to set staffing baselines:
      • Convert average jobs/month into jobs/week and jobs/day.
      • Compare to team throughput capacity to estimate slack or overload.
      • Pair with volatility (peak month vs average month) to size surge buffers.

      Operational interpretation:
      • Stable average with high peaks: needs flexible surge staffing.
      • Rising average for 3+ months: hire or rebalance before SLA drift starts.
      • Falling average with constant headcount: margin compression risk.

      Click to inspect month-by-month distribution.`,
        onClick: () => openVolumeDrillDown(data),
      },
      {
        title: "Avg Turnaround",
        value: `${data.summary.avgTurnaround.toFixed(1)} days`,
        subtitle: "Translation delivery cycle",
        tooltipTitle: "Average Turnaround",
        tooltipContent: `Average Turnaround = average days from request date to delivery date for translation work.

Interpretation guide:
• Lower is usually better, but very low can imply rush-heavy, high-stress operations.
• Rising values can indicate intake bottlenecks, production queue buildup, or assignment mismatch.

Root-cause checks when turnaround worsens:
1) Is volume growth outpacing linguist capacity?
2) Are complex jobs increasing (specialized domains, longer files)?
3) Are reassignment/rework loops increasing?
4) Is client approval lag delaying close-out?

Click this card to review individual turnaround records.`,
        onClick: () =>
          openDrillDown({
            title: "Turnaround Details",
            subtitle: `${data.turnaroundRows.length} translation jobs`,
            description:
              "Detailed request-to-delivery records. Sort by turnaround to isolate long-tail outliers and delay clusters.",
            data: [...data.turnaroundRows].sort(
              (a, b) => (b.turnaroundDays || 0) - (a.turnaroundDays || 0),
            ),
            columns: TURNAROUND_COLUMNS,
          }),
      },
      {
        title: "On-Time Rate",
        value: formatPercent(data.summary.onTimeRate),
        subtitle: "Delivered on/before deadline",
        tooltipTitle: "On-Time Rate",
        tooltipContent: `On-Time Rate = (Delivered on/before deadline / Total delivered translation jobs) x 100.

Benchmark perspective:
• 95%+ strong reliability
• 90-95% acceptable, monitor at client level
• <90% service risk zone

Use this with cancellation and completion metrics:
• High completion + low on-time = team finishes work, but too late.
• Low completion + low on-time = deeper scheduling/assignment breakdown.

Click to open late-job details and identify recurring bottlenecks.`,
        color: "success.main",
        onClick: () =>
          openDrillDown({
            title: "Late Delivery Exceptions",
            subtitle: `${formatPercent(100 - data.summary.onTimeRate)} late or missed deadline`,
            description:
              "These are deadline exceptions across translation jobs. Prioritize by client impact and turnaround severity.",
            data: data.turnaroundRows.filter((row) => !row.onTime),
            columns: TURNAROUND_COLUMNS,
          }),
      },
      {
        title: "Translation Share",
        value: formatPercent(translationShare),
        subtitle: `${formatNumber(data.categoryTotals.translation)} jobs`,
        tooltipTitle: "Translation Share",
        tooltipContent: `Translation Share = (Translation jobs / Total jobs) x 100.

      Strategic use:
      • Indicates workload allocation between language-production work and live interpretation.
      • A rising share may require stronger editorial QA and turnaround controls.
      • A falling share can increase scheduling complexity if interpretation rises.

      Capacity action:
      • Track this monthly and adjust team mix, vendor pool, and tools accordingly.`,
        onClick: () => openVolumeDrillDown(data),
      },
      {
        title: "Interpretation Share",
        value: formatPercent(interpretationShare),
        subtitle: `${formatNumber(data.categoryTotals.interpretation)} jobs`,
        tooltipTitle: "Interpretation Share",
        tooltipContent: `Interpretation Share = (Interpretation jobs / Total jobs) x 100.

Operational implications:
• Higher interpretation share increases real-time scheduling sensitivity.
• Misses and late fills become more expensive and visible to clients.
• Demand spikes often require deeper backup coverage by language/time slot.

Watch for:
• Rising interpretation share + rising cancellation = fill-rate pressure.
• Rising interpretation share + stable completion = strong dispatch execution.`,
        onClick: () => openVolumeDrillDown(data),
      },
    ];
  }, [openDrillDown, openVolumeDrillDown]);

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, lg: 8 }}>
          <ReportLineChart
            title="Monthly Service Volume"
            tooltipTitle="Monthly Service Volume"
            tooltipContent="Tracks overall service demand and category mix over time. Sustained growth without throughput improvements usually creates SLA pressure."
            xAxisData={data.monthlyVolume.map((row) => row.monthLabel)}
            series={[
              {
                label: "Total",
                data: data.monthlyVolume.map((row) => row.total),
                color: chartColors.primary,
              },
              {
                label: "Translation",
                data: data.monthlyVolume.map((row) => row.translation),
                color: chartColors.info,
              },
              {
                label: "Interpretation",
                data: data.monthlyVolume.map((row) => row.interpretation),
                color: chartColors.success,
              },
            ]}
            yAxisLabel="Services"
            valueFormatter={(value) => formatNumber(value)}
            height={320}
            onMarkClick={(dataIndex) => {
              const item = data.monthlyVolume[dataIndex];
              if (!item) return;

              openDrillDown({
                title: `Service Volume for ${item.monthLabel}`,
                subtitle: `${formatNumber(item.total)} total services`,
                description:
                  "Single-month breakdown for demand composition and category balance.",
                data: [item],
                columns: VOLUME_COLUMNS,
              })
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, lg: 4 }}>
          <ReportPieChart
            title="Service Category Mix"
            tooltipTitle="Service Category Mix"
            tooltipContent="Current workload distribution by major service category. Use this to understand where operations bandwidth is being consumed."
            data={[
              { id: 1, label: "Translation", value: data.categoryTotals.translation },
              { id: 2, label: "Interpretation", value: data.categoryTotals.interpretation },
              { id: 3, label: "Other", value: data.categoryTotals.other },
            ]}
            valueFormatter={(value) => `${formatNumber(value)} jobs`}
            height={320}
            onSliceClick={(slice) => {
              const categoryField =
                slice.label === "Translation"
                  ? "translation"
                  : slice.label === "Interpretation"
                    ? "interpretation"
                    : "other";

              openDrillDown({
                title: `${slice.label} Monthly Trend`,
                subtitle: `${formatNumber(slice.value)} total ${slice.label.toLowerCase()} services`,
                description:
                  "Monthly contribution for the selected category to evaluate concentration and seasonal demand changes.",
                data: data.monthlyVolume.map((row) => ({
                  monthLabel: row.monthLabel,
                  total: row.total,
                  selectedCategory: row[categoryField] || 0,
                })),
                columns: [
                  { field: "monthLabel", headerName: "Month", width: 130 },
                  { field: "selectedCategory", headerName: `${slice.label} Jobs`, width: 140 },
                  { field: "total", headerName: "Total Jobs", width: 110 },
                ],
              });
            }}
          />
        </Grid>
      </Grid>
    ),
    [chartColors.info, chartColors.primary, chartColors.success, openDrillDown],
  );

  return (
    <>
      <DashboardReport
        title="Service Operations Dashboard"
        description="Core service throughput, turnaround, and execution health across the selected period."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

export const SchedulingTimelineUrgency = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const { drillDown, closeDrillDown, openDrillDown } = useServiceOpsDrillDown();

  const dataFetchFn = useCallback((activeFilters) => getServiceOpsData(activeFilters), []);

  const metricsConfig = useCallback((data) => {
    const total = data.turnaroundRows.length || 1;

    return [
      {
        title: "Fast Turnaround (<=2d)",
        value: formatPercent((data.fastTurnaroundCount / total) * 100),
        subtitle: `${formatNumber(data.fastTurnaroundCount)} jobs`,
        tooltipTitle: "Fast Turnaround",
        tooltipContent: `Fast Turnaround Rate = (Jobs delivered in <= 2 days / Total translation jobs) x 100.

Interpretation:
• Higher rate improves responsiveness and client satisfaction.
• But a very high rate may indicate rush-driven operations and potential team fatigue.

Recommended checks:
• Track whether fast-turnaround growth also increases late/cancelled jobs.
• Validate pricing and margin protection for urgent jobs.
• Ensure repeat rush clients are not masking process weaknesses.

Click to inspect all <=2 day deliveries.`,
        onClick: () =>
          openDrillDown({
            title: "Fast Turnaround Jobs (<=2 days)",
            subtitle: `${formatNumber(data.fastTurnaroundCount)} jobs`,
            description:
              "These jobs were delivered within two days. Review for workload concentration and sustainability.",
            data: data.turnaroundRows.filter((row) => row.turnaroundDays <= 2),
            columns: TURNAROUND_COLUMNS,
          }),
      },
      {
        title: "Extended Turnaround (>5d)",
        value: formatPercent((data.longTurnaroundCount / total) * 100),
        subtitle: `${formatNumber(data.longTurnaroundCount)} jobs`,
        tooltipTitle: "Extended Turnaround",
        tooltipContent: `Extended Turnaround Rate = (Jobs taking > 5 days / Total translation jobs) x 100.

Why it matters:
• Captures complexity and queue buildup pressure.
• Persistent growth in this bucket often precedes SLA misses.

Root-cause workflow:
1) Separate complex jobs from standard jobs.
2) Check assignment lag (request to first action).
3) Check rework loops and QA handoff delays.
4) Check client-side waiting periods.

Click to review all >5 day jobs.`,
        onClick: () =>
          openDrillDown({
            title: "Extended Turnaround Jobs (>5 days)",
            subtitle: `${formatNumber(data.longTurnaroundCount)} jobs`,
            description:
              "Long-cycle jobs that may indicate complexity concentration, queueing, or handoff delays.",
            data: [...data.turnaroundRows]
              .filter((row) => row.turnaroundDays > 5)
              .sort((a, b) => (b.turnaroundDays || 0) - (a.turnaroundDays || 0)),
            columns: TURNAROUND_COLUMNS,
          }),
      },
      {
        title: "Late to Deadline",
        value: formatPercent((data.lateTurnaroundCount / total) * 100),
        subtitle: `${formatNumber(data.lateTurnaroundCount)} jobs`,
        tooltipTitle: "Late to Deadline",
        tooltipContent: `Late-to-Deadline Rate = (Jobs delivered after deadline / Total translation jobs) x 100.

Operational meaning:
• Core reliability signal for client trust and renewal risk.
• Often driven by assignment delays, insufficient backups, or late-stage rework.

Use this as an intervention queue:
• Prioritize high-revenue clients with repeated late deliveries.
• Cross-check with monthly volume spikes.
• Track whether process improvements reduce this within 30-60 days.

Click to open late-job details.`,
        color: "warning.main",
        onClick: () =>
          openDrillDown({
            title: "Late to Deadline Jobs",
            subtitle: `${formatNumber(data.lateTurnaroundCount)} jobs`,
            description:
              "Jobs delivered after deadline. Sort by turnaround and client to prioritize root-cause remediation.",
            data: data.turnaroundRows.filter((row) => !row.onTime),
            columns: TURNAROUND_COLUMNS,
          }),
      },
      {
        title: "Avg Turnaround",
        value: `${data.summary.avgTurnaround.toFixed(1)} days`,
        subtitle: "Request to delivery",
        tooltipTitle: "Average Turnaround",
        tooltipContent: `Average elapsed cycle time from request to delivery.

Use this with distribution metrics:
• Average can hide long-tail outliers.
• Keep an eye on both average and >5-day share.
• Rising average with stable on-time can still indicate growing complexity.

Click to open all turnaround records.`,
        onClick: () =>
          openDrillDown({
            title: "All Turnaround Records",
            subtitle: `${formatNumber(data.turnaroundRows.length)} jobs`,
            description:
              "Complete turnaround detail list for cycle-time diagnostics and client pattern analysis.",
            data: data.turnaroundRows,
            columns: TURNAROUND_COLUMNS,
          }),
      },
    ];
  }, [openDrillDown]);

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportLineChart
            title="On-Time Trend"
            tooltipTitle="On-Time Trend"
            tooltipContent="Month-over-month on-time delivery trend. A downward slope is an early warning for operational strain."
            xAxisData={data.monthlyTurnaround.map((row) => row.monthLabel)}
            series={[
              {
                label: "On-Time Rate",
                data: data.monthlyTurnaround.map((row) => row.onTimeRate),
                color: chartColors.success,
              },
            ]}
            yAxisLabel="On-Time %"
            valueFormatter={(value) => `${(value || 0).toFixed(1)}%`}
            height={320}
            onMarkClick={(dataIndex) => {
              const item = data.monthlyTurnaround[dataIndex];
              if (!item) return;

              openDrillDown({
                title: `On-Time Performance: ${item.monthLabel}`,
                subtitle: `${formatPercent(item.onTimeRate)} on-time`,
                description:
                  "Monthly reliability snapshot for focused timeline diagnostics.",
                data: [item],
                columns: [
                  { field: "monthLabel", headerName: "Month", width: 140 },
                  { field: "servicesCount", headerName: "Services", width: 100 },
                  {
                    field: "onTimeRate",
                    headerName: "On-Time Rate",
                    width: 120,
                    renderCell: (row) => `${(row.onTimeRate || 0).toFixed(1)}%`,
                  },
                  {
                    field: "avgTurnaround",
                    headerName: "Avg Turnaround",
                    width: 130,
                    renderCell: (row) => `${(row.avgTurnaround || 0).toFixed(1)} days`,
                  },
                ],
              })
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, md: 6 }}>
          <ReportBarChart
            title="Average Turnaround by Month"
            tooltipTitle="Average Turnaround by Month"
            tooltipContent="Shows turnaround cycle in days by month. Use with on-time trend to separate deadline misses from complexity-driven cycle changes."
            data={data.monthlyTurnaround}
            xAxisKey="monthLabel"
            series={[
              {
                dataKey: "avgTurnaround",
                label: "Avg Days",
                color: chartColors.info,
                valueFormatter: (value) => `${(value || 0).toFixed(1)} days`,
              },
            ]}
            yAxisLabel="Days"
            valueFormatter={(value) => `${(value || 0).toFixed(1)} days`}
            height={320}
            yAxisScaleType="linear"
            onBarClick={(item) =>
              openDrillDown({
                title: `Cycle Time: ${item.monthLabel}`,
                subtitle: `${(item.avgTurnaround || 0).toFixed(1)} average days`,
                description:
                  "Month-level turnaround details to compare cycle-time efficiency across periods.",
                data: [item],
                columns: [
                  { field: "monthLabel", headerName: "Month", width: 140 },
                  { field: "servicesCount", headerName: "Services", width: 100 },
                  {
                    field: "avgTurnaround",
                    headerName: "Avg Turnaround",
                    width: 130,
                    renderCell: (row) => `${(row.avgTurnaround || 0).toFixed(1)} days`,
                  },
                  {
                    field: "onTimeRate",
                    headerName: "On-Time Rate",
                    width: 120,
                    renderCell: (row) => `${(row.onTimeRate || 0).toFixed(1)}%`,
                  },
                ],
              })
            }
          />
        </Grid>
      </Grid>
    ),
    [chartColors.info, chartColors.success, openDrillDown],
  );

  return (
    <>
      <DashboardReport
        title="Scheduling, Timeline & Urgency"
        description="Turnaround speed, on-time stability, and deadline pressure indicators for service execution."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

export const ServiceMixDeliveryMode = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const { drillDown, closeDrillDown, openDrillDown } = useServiceOpsDrillDown();

  const dataFetchFn = useCallback((activeFilters) => getServiceOpsData(activeFilters), []);

  const metricsConfig = useCallback((data) => {
    const topType = [...data.servicesByType].sort(
      (a, b) => b.servicesCount - a.servicesCount,
    )[0];

    const interpretationTotal = data.deliveryModeMix.remote + data.deliveryModeMix.onSite + data.deliveryModeMix.hybridOrUnknown;
    const remoteShare = interpretationTotal > 0
      ? (data.deliveryModeMix.remote / interpretationTotal) * 100
      : 0;

    return [
      {
        title: "Service Types Active",
        value: formatNumber(data.servicesByType.length),
        subtitle: "Distinct service types in period",
        tooltipTitle: "Service Types Active",
        tooltipContent: `Distinct count of service types delivered in the selected period.

Why it matters:
• More variety can increase routing complexity, QA variation, and training requirements.
• Lower variety may improve specialization and repeatable throughput.

Operational action:
• Review whether each active type has enough recurring volume to justify dedicated capacity.
• Deprioritize low-value, high-friction service types if they create operational drag.

Click to open full service-type performance detail.`,
        onClick: () =>
          openDrillDown({
            title: "Service Type Portfolio",
            subtitle: `${formatNumber(data.servicesByType.length)} active service types`,
            description:
              "Detailed performance by service type, including volume, revenue, and margin.",
            data: data.servicesByType,
            columns: SERVICE_TYPE_COLUMNS,
          }),
      },
      {
        title: "Top Service Type",
        value: topType?.serviceType || "-",
        subtitle: `${formatNumber(topType?.servicesCount || 0)} jobs`,
        tooltipTitle: "Top Service Type",
        tooltipContent: `Most frequently delivered service type by job count in the selected period.

Use this metric to:
• Validate where your operations engine spends most effort.
• Detect demand shifts early and adjust staffing/training.
• Compare top-volume type against top-margin type to avoid volume traps.

If the top type has low margin, investigate pricing, cost structure, or process design.

Click to view all service types ranked by volume.`,
        onClick: () =>
          openDrillDown({
            title: "Service Types Ranked by Volume",
            subtitle: `Top type: ${topType?.serviceType || "-"}`,
            description:
              "Ranking view to compare type concentration and evaluate portfolio risk.",
            data: [...data.servicesByType].sort(
              (a, b) => (b.servicesCount || 0) - (a.servicesCount || 0),
            ),
            columns: SERVICE_TYPE_COLUMNS,
          }),
      },
      {
        title: "Remote Interpretation Share",
        value: formatPercent(remoteShare),
        subtitle: `${formatNumber(data.deliveryModeMix.remote)} remote jobs`,
        tooltipTitle: "Remote Interpretation Share",
        tooltipContent: `Estimated share of interpretation jobs delivered remotely (virtual/phone/OPI categories).

Strategic implications:
• Higher remote share may lower travel overhead and expand scheduling flexibility.
• But it can also shift quality risks to technology and coordination reliability.

Use with on-site counts to balance staffing model and client delivery expectations.

Click to view interpretation mode breakdown.`,
        onClick: () =>
          openDrillDown({
            title: "Interpretation Delivery Modes",
            subtitle: `${formatPercent(remoteShare)} remote share`,
            description:
              "Mode mix derived from interpretation type names for staffing and channel strategy.",
            data: [
              { mode: "Remote", jobs: data.deliveryModeMix.remote },
              { mode: "On-Site", jobs: data.deliveryModeMix.onSite },
              { mode: "Hybrid/Unspecified", jobs: data.deliveryModeMix.hybridOrUnknown },
            ],
            columns: [
              { field: "mode", headerName: "Mode", width: 180 },
              { field: "jobs", headerName: "Jobs", width: 100 },
            ],
          }),
      },
      {
        title: "On-Site Interpretation",
        value: formatNumber(data.deliveryModeMix.onSite),
        subtitle: "In-person interpretation jobs",
        tooltipTitle: "On-Site Interpretation",
        tooltipContent: `Count of interpretation jobs delivered in-person.

Operational impacts:
• In-person volume increases scheduling friction and backup coverage requirements.
• Travel and punctuality risks become more material at higher on-site share.

If on-site volume is growing, proactively increase travel-ready linguist coverage and confirmation discipline.

Click to inspect interpretation mode detail.`,
        onClick: () =>
          openDrillDown({
            title: "Interpretation Delivery Modes",
            subtitle: `${formatNumber(data.deliveryModeMix.onSite)} on-site jobs`,
            description:
              "Mode mix view to support in-person staffing and scheduling decisions.",
            data: [
              { mode: "Remote", jobs: data.deliveryModeMix.remote },
              { mode: "On-Site", jobs: data.deliveryModeMix.onSite },
              { mode: "Hybrid/Unspecified", jobs: data.deliveryModeMix.hybridOrUnknown },
            ],
            columns: [
              { field: "mode", headerName: "Mode", width: 180 },
              { field: "jobs", headerName: "Jobs", width: 100 },
            ],
          }),
      },
    ];
  }, [openDrillDown]);

  const chartsConfig = useCallback(
    (data) => {
      const topServiceTypes = [...data.servicesByType]
        .sort((a, b) => b.servicesCount - a.servicesCount)
        .slice(0, 10);

      return (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, lg: 8 }}>
            <ReportBarChart
              title="Top Service Types by Volume"
              tooltipTitle="Top Service Types by Volume"
              tooltipContent="Top 10 service types by number of jobs delivered. Use this for staffing strategy and training priorities."
              data={topServiceTypes}
              xAxisKey="serviceType"
              series={[
                {
                  dataKey: "servicesCount",
                  label: "Jobs",
                  color: chartColors.primary,
                  valueFormatter: (value) => `${formatNumber(value)} jobs`,
                },
              ]}
              yAxisLabel="Jobs"
              valueFormatter={(value) => `${formatNumber(value)} jobs`}
              height={340}
              yAxisScaleType="linear"
              onBarClick={(item) =>
                openDrillDown({
                  title: `Service Type: ${item.serviceType}`,
                  subtitle: `${formatNumber(item.servicesCount)} jobs`,
                  description:
                    "Detailed view for a single service type including volume and financial contribution.",
                  data: [item],
                  columns: SERVICE_TYPE_COLUMNS,
                })
              }
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <ReportPieChart
              title="Interpretation Delivery Mode"
              tooltipTitle="Interpretation Delivery Mode"
              tooltipContent="Interpretation mode split inferred from service-type labels (virtual/phone as remote, in-person as on-site)."
              data={[
                { id: 1, label: "Remote", value: data.deliveryModeMix.remote },
                { id: 2, label: "On-Site", value: data.deliveryModeMix.onSite },
                {
                  id: 3,
                  label: "Hybrid/Unspecified",
                  value: data.deliveryModeMix.hybridOrUnknown,
                },
              ]}
              valueFormatter={(value) => `${formatNumber(value)} jobs`}
              height={340}
              onSliceClick={(slice) =>
                openDrillDown({
                  title: `${slice.label} Interpretation Mode`,
                  subtitle: `${formatNumber(slice.value)} jobs`,
                  description:
                    "Selected delivery mode detail from interpretation mix distribution.",
                  data: [
                    { mode: "Remote", jobs: data.deliveryModeMix.remote },
                    { mode: "On-Site", jobs: data.deliveryModeMix.onSite },
                    { mode: "Hybrid/Unspecified", jobs: data.deliveryModeMix.hybridOrUnknown },
                  ],
                  columns: [
                    { field: "mode", headerName: "Mode", width: 180 },
                    { field: "jobs", headerName: "Jobs", width: 100 },
                  ],
                })
              }
            />
          </Grid>
        </Grid>
      );
    },
    [chartColors.primary, openDrillDown],
  );

  return (
    <>
      <DashboardReport
        title="Service Mix & Delivery Mode"
        description="Service-type demand distribution and interpretation delivery-mode mix for capacity and staffing decisions."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

export const DurationEffortTravel = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const { drillDown, closeDrillDown, openDrillDown } = useServiceOpsDrillDown();

  const dataFetchFn = useCallback((activeFilters) => getServiceOpsData(activeFilters), []);

  const metricsConfig = useCallback((data) => {
    const topSlowClients = [...data.turnaroundRows]
      .sort((a, b) => b.turnaroundDays - a.turnaroundDays)
      .slice(0, 5);

    const avgSlowestTop5 =
      topSlowClients.length > 0
        ? topSlowClients.reduce((sum, row) => sum + row.turnaroundDays, 0) /
          topSlowClients.length
        : 0;

    return [
      {
        title: "Avg Turnaround Days",
        value: `${data.summary.avgTurnaround.toFixed(1)} days`,
        subtitle: "Primary cycle-time indicator",
        tooltipTitle: "Average Turnaround Days",
        tooltipContent: `Primary cycle-time proxy for operational effort and flow quality.

Interpretation:
• Rising average can reflect complexity growth, queueing, or assignment latency.
• Stable average with worsening long-tail can hide severe outliers.

Use alongside long-cycle and slowest-tail metrics to avoid relying on averages alone.

Click to inspect turnaround records.`,
        onClick: () =>
          openDrillDown({
            title: "Cycle-Time Detail",
            subtitle: `${data.summary.avgTurnaround.toFixed(1)} average days`,
            description:
              "Turnaround record detail for cycle-time and long-tail diagnostics.",
            data: data.turnaroundRows,
            columns: TURNAROUND_COLUMNS,
          }),
      },
      {
        title: "Long-Cycle Jobs",
        value: formatNumber(data.longTurnaroundCount),
        subtitle: "Jobs over 5 days turnaround",
        tooltipTitle: "Long-Cycle Jobs",
        tooltipContent: `Long-Cycle Jobs = count of translation jobs with turnaround > 5 days.

Why this is critical:
• Captures long-tail risk hidden by averages.
• Long-tail jobs are often where client escalations originate.

Root-cause areas:
• Assignment lag
• Specialist scarcity
• Rework/QA loops
• Client-side waiting dependencies

Click to open all long-cycle jobs ranked by delay.`,
        color: "warning.main",
        onClick: () =>
          openDrillDown({
            title: "Long-Cycle Jobs (>5 days)",
            subtitle: `${formatNumber(data.longTurnaroundCount)} jobs`,
            description:
              "Long-turnaround jobs sorted from highest to lowest for rapid triage.",
            data: [...data.turnaroundRows]
              .filter((row) => row.turnaroundDays > 5)
              .sort((a, b) => (b.turnaroundDays || 0) - (a.turnaroundDays || 0)),
            columns: TURNAROUND_COLUMNS,
          }),
      },
      {
        title: "Fast-Cycle Jobs",
        value: formatNumber(data.fastTurnaroundCount),
        subtitle: "Jobs delivered in 2 days or less",
        tooltipTitle: "Fast-Cycle Jobs",
        tooltipContent: `Fast-Cycle Jobs = count of translation jobs delivered in <= 2 days.

Use this to:
• Monitor urgent-service throughput capacity.
• Evaluate whether rush demand is becoming structurally high.
• Ensure quick-turn work is not harming quality or team sustainability.

Click to view all fast-cycle deliveries.`,
        onClick: () =>
          openDrillDown({
            title: "Fast-Cycle Jobs (<=2 days)",
            subtitle: `${formatNumber(data.fastTurnaroundCount)} jobs`,
            description:
              "Fast-turnaround jobs used for urgent-capacity and sustainability analysis.",
            data: data.turnaroundRows.filter((row) => row.turnaroundDays <= 2),
            columns: TURNAROUND_COLUMNS,
          }),
      },
      {
        title: "Avg of Slowest 5",
        value: `${avgSlowestTop5.toFixed(1)} days`,
        subtitle: "Slow-tail complexity signal",
        tooltipTitle: "Average of Slowest 5 Jobs",
        tooltipContent: `Average turnaround for the five slowest jobs in period.

Why this metric is powerful:
• Quantifies the severity of long-tail exceptions.
• Tracks whether worst-case outcomes are improving over time.

If this remains high while average improves, hidden risk still exists.

Click to inspect the slowest records directly.`,
        onClick: () =>
          openDrillDown({
            title: "Slowest 5 Jobs",
            subtitle: `${avgSlowestTop5.toFixed(1)} average days`,
            description:
              "Highest-turnaround records that represent long-tail execution risk.",
            data: [...data.turnaroundRows]
              .sort((a, b) => (b.turnaroundDays || 0) - (a.turnaroundDays || 0))
              .slice(0, 5),
            columns: TURNAROUND_COLUMNS,
          }),
      },
    ];
  }, [openDrillDown]);

  const chartsConfig = useCallback(
    (data) => {
      const turnaroundDistribution = [
        {
          id: 1,
          label: "0-2 days",
          value: data.turnaroundRows.filter((row) => row.turnaroundDays <= 2).length,
        },
        {
          id: 2,
          label: "3-5 days",
          value: data.turnaroundRows.filter(
            (row) => row.turnaroundDays > 2 && row.turnaroundDays <= 5,
          ).length,
        },
        {
          id: 3,
          label: "6-10 days",
          value: data.turnaroundRows.filter(
            (row) => row.turnaroundDays > 5 && row.turnaroundDays <= 10,
          ).length,
        },
        {
          id: 4,
          label: ">10 days",
          value: data.turnaroundRows.filter((row) => row.turnaroundDays > 10).length,
        },
      ];

      return (
        <Grid container spacing={2}>
          <Grid size={{ xs: 12, md: 5 }}>
            <ReportPieChart
              title="Turnaround Distribution"
              tooltipTitle="Turnaround Distribution"
              tooltipContent="Cycle-time distribution across turnaround buckets to highlight workload complexity and long-tail risk."
              data={turnaroundDistribution}
              valueFormatter={(value) => `${formatNumber(value)} jobs`}
              height={320}
              onSliceClick={(slice) =>
                openDrillDown({
                  title: `Turnaround Bucket: ${slice.label}`,
                  subtitle: `${formatNumber(slice.value)} jobs`,
                  description:
                    "Jobs in the selected turnaround bucket for distribution and exception analysis.",
                  data: data.turnaroundRows.filter((row) => {
                    if (slice.label === "0-2 days") return row.turnaroundDays <= 2;
                    if (slice.label === "3-5 days")
                      return row.turnaroundDays > 2 && row.turnaroundDays <= 5;
                    if (slice.label === "6-10 days")
                      return row.turnaroundDays > 5 && row.turnaroundDays <= 10;
                    return row.turnaroundDays > 10;
                  }),
                  columns: TURNAROUND_COLUMNS,
                })
              }
            />
          </Grid>
          <Grid size={{ xs: 12, md: 7 }}>
            <ReportBarChart
              title="Longest Turnaround by Client"
              tooltipTitle="Longest Turnaround by Client"
              tooltipContent="Top client jobs with longest turnaround. Use this to identify where execution delay is concentrated."
              data={[...data.turnaroundRows]
                .sort((a, b) => b.turnaroundDays - a.turnaroundDays)
                .slice(0, 8)
                .map((row, index) => ({
                  id: `${row.clientName}-${index}`,
                  clientName: row.clientName || "Unknown",
                  turnaroundDays: row.turnaroundDays,
                }))}
              xAxisKey="clientName"
              series={[
                {
                  dataKey: "turnaroundDays",
                  label: "Turnaround Days",
                  color: chartColors.warning,
                  valueFormatter: (value) => `${(value || 0).toFixed(1)} days`,
                },
              ]}
              yAxisLabel="Days"
              valueFormatter={(value) => `${(value || 0).toFixed(1)} days`}
              height={320}
              yAxisScaleType="linear"
              onBarClick={(item) =>
                openDrillDown({
                  title: `Client Delay Detail: ${item.clientName}`,
                  subtitle: `${(item.turnaroundDays || 0).toFixed(1)} turnaround days`,
                  description:
                    "Top delay instances for this client to support account-level remediation planning.",
                  data: data.turnaroundRows.filter(
                    (row) => (row.clientName || "Unknown") === item.clientName,
                  ),
                  columns: TURNAROUND_COLUMNS,
                })
              }
            />
          </Grid>
        </Grid>
      );
    },
    [chartColors.warning, openDrillDown],
  );

  return (
    <>
      <DashboardReport
        title="Duration, Effort & Travel"
        description="Cycle-time distribution and slow-tail analysis as operational effort proxies for service execution quality."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

export const ServiceReliabilityExceptions = ({ filters }) => {
  const theme = useTheme();
  const chartColors = getChartColors(theme);
  const { drillDown, closeDrillDown, openDrillDown } = useServiceOpsDrillDown();

  const dataFetchFn = useCallback((activeFilters) => getServiceOpsData(activeFilters), []);

  const metricsConfig = useCallback((data) => {
    const highRiskClients = data.reliabilityRows.filter(
      (row) => (row.riskLevel || "").toLowerCase() === "high",
    );

    return [
      {
        title: "Cancellation Rate",
        value: formatPercent(data.summary.cancellationRate),
        subtitle: "Cancelled services as % of all services",
        tooltipTitle: "Cancellation Rate",
        tooltipContent: `Cancellation Rate = (Cancelled services / Total services) x 100.

Why this matters:
• Direct indicator of preventable disruption and revenue leakage.
• Rising cancellation usually precedes client dissatisfaction and planner overload.

Diagnosis sequence:
1) Check client-level concentration of cancellations.
2) Check service-type and lead-time patterns.
3) Check fill failures and late confirmations.

Click to view clients with the highest cancellation pressure.`,
        color: "warning.main",
        onClick: () =>
          openDrillDown({
            title: "Clients by Cancellation Pressure",
            subtitle: `${formatPercent(data.summary.cancellationRate)} overall cancellation rate`,
            description:
              "Client-level cancellation rates ranked highest first for intervention prioritization.",
            data: [...data.reliabilityRows].sort(
              (a, b) => (b.cancellationRate || 0) - (a.cancellationRate || 0),
            ),
            columns: RELIABILITY_COLUMNS,
          }),
      },
      {
        title: "Completion Rate",
        value: formatPercent(data.summary.completionRate),
        subtitle: "Services completed vs scheduled",
        tooltipTitle: "Completion Rate",
        tooltipContent: `Completion Rate = (Completed services / Total services) x 100.

How to use it:
• Core execution reliability KPI.
• Declines can indicate staffing strain, process friction, or assignment quality issues.

Cross-metric interpretation:
• High completion + low on-time = work is completed but too late.
• Low completion + high cancellation = fill and planning risk.

Click to inspect client-level completion performance.`,
        color: "success.main",
        onClick: () =>
          openDrillDown({
            title: "Completion Reliability by Client",
            subtitle: `${formatPercent(data.summary.completionRate)} completion rate`,
            description:
              "Client-level completion trends to locate operational reliability pockets and risks.",
            data: [...data.reliabilityRows].sort(
              (a, b) => (a.completionRate || 0) - (b.completionRate || 0),
            ),
            columns: RELIABILITY_COLUMNS,
          }),
      },
      {
        title: "Late Delivery Rate",
        value: formatPercent(data.summary.lateRate),
        subtitle: `Avg late days ${data.summary.avgLateDays.toFixed(1)}`,
        tooltipTitle: "Late Delivery Rate",
        tooltipContent: `Late Delivery Rate = (Late services / SLA-tracked services) x 100.

Why it matters:
• Captures delivery reliability risk not visible from completion alone.
• Key signal for retention and account health in SLA-sensitive clients.

Action framework:
• Prioritize clients with both high late rate and high revenue.
• Inspect lead time, reassignment frequency, and confirmation delays.
• Track 30-day post-fix trend to validate interventions.

Click to open high-late client detail.`,
        onClick: () =>
          openDrillDown({
            title: "Late Delivery Risk by Client",
            subtitle: `${formatPercent(data.summary.lateRate)} late delivery rate`,
            description:
              "Client-level lateness profile to target SLA recovery actions.",
            data: [...data.reliabilityRows].sort(
              (a, b) => (b.avgLateDays || 0) - (a.avgLateDays || 0),
            ),
            columns: RELIABILITY_COLUMNS,
          }),
      },
      {
        title: "High-Risk Clients",
        value: formatNumber(highRiskClients.length),
        subtitle: "Accounts with elevated SLA risk",
        tooltipTitle: "High-Risk Clients",
        tooltipContent: `Count of clients flagged as high risk by combined SLA reliability signals.

Risk signals include:
• Low completion
• Low on-time delivery
• Elevated cancellations
• Higher average late days

Use as an intervention queue:
• Prioritize by revenue at risk and trend deterioration.
• Assign clear account owner and recovery plan.
• Track risk count monthly for control effectiveness.

Click to open all high-risk clients.`,
        color: "error.main",
        onClick: () =>
          openDrillDown({
            title: "High-Risk Client Queue",
            subtitle: `${formatNumber(highRiskClients.length)} high-risk clients`,
            description:
              "Client list requiring immediate operational and account-management intervention.",
            data: highRiskClients,
            columns: RELIABILITY_COLUMNS,
            summary: {
              metrics: [
                { label: "High-Risk Clients", value: formatNumber(highRiskClients.length), color: "error.main" },
                {
                  label: "Revenue At Risk",
                  value: formatCurrency(
                    highRiskClients.reduce((sum, row) => sum + Number(row.revenue || 0), 0),
                  ),
                  color: "warning.main",
                },
              ],
            },
          }),
      },
    ];
  }, [openDrillDown]);

  const chartsConfig = useCallback(
    (data) => (
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 5 }}>
          <ReportPieChart
            title="Exception Signal Mix"
            tooltipTitle="Exception Signal Mix"
            tooltipContent="Exception composition using cancellations, late deliveries, and healthy completions."
            data={[
              {
                id: 1,
                label: "Cancelled",
                value: Number(((data.summary.cancellationRate / 100) * data.summary.totalServices || 0).toFixed(0)),
              },
              {
                id: 2,
                label: "Late",
                value: Number(((data.summary.lateRate / 100) * data.summary.totalServices || 0).toFixed(0)),
              },
              {
                id: 3,
                label: "Completed On Plan",
                value: Number(
                  Math.max(
                    data.summary.totalServices -
                      ((data.summary.cancellationRate / 100) * data.summary.totalServices || 0) -
                      ((data.summary.lateRate / 100) * data.summary.totalServices || 0),
                    0,
                  ).toFixed(0),
                ),
              },
            ]}
            valueFormatter={(value) => `${formatNumber(value)} services`}
            height={320}
            onSliceClick={(slice) =>
              openDrillDown({
                title: `Exception Signal: ${slice.label}`,
                subtitle: `${formatNumber(slice.value)} services`,
                description:
                  "Signal breakdown to compare disruption mix across cancellations, lateness, and healthy completions.",
                data: [
                  { signal: "Cancelled", services: Number(((data.summary.cancellationRate / 100) * data.summary.totalServices || 0).toFixed(0)) },
                  { signal: "Late", services: Number(((data.summary.lateRate / 100) * data.summary.totalServices || 0).toFixed(0)) },
                  {
                    signal: "Completed On Plan",
                    services: Number(
                      Math.max(
                        data.summary.totalServices -
                          ((data.summary.cancellationRate / 100) * data.summary.totalServices || 0) -
                          ((data.summary.lateRate / 100) * data.summary.totalServices || 0),
                        0,
                      ).toFixed(0),
                    ),
                  },
                ],
                columns: [
                  { field: "signal", headerName: "Signal", width: 180 },
                  { field: "services", headerName: "Services", width: 110 },
                ],
              })
            }
          />
        </Grid>
        <Grid size={{ xs: 12, md: 7 }}>
          <ReportBarChart
            title="Top Clients by Cancellation Rate"
            tooltipTitle="Top Clients by Cancellation Rate"
            tooltipContent="Client-level cancellation pressure. Focus recovery actions on high-rate, high-volume accounts first."
            data={[...data.reliabilityRows]
              .sort((a, b) => (b.cancellationRate || 0) - (a.cancellationRate || 0))
              .slice(0, 10)}
            xAxisKey="clientName"
            series={[
              {
                dataKey: "cancellationRate",
                label: "Cancellation %",
                color: chartColors.error,
                valueFormatter: (value) => `${(value || 0).toFixed(1)}%`,
              },
            ]}
            yAxisLabel="Cancellation %"
            valueFormatter={(value) => `${(value || 0).toFixed(1)}%`}
            height={320}
            yAxisScaleType="linear"
            onBarClick={(item) =>
              openDrillDown({
                title: `Client Reliability: ${item.clientName}`,
                subtitle: `${(item.cancellationRate || 0).toFixed(1)}% cancellation`,
                description:
                  "Client-level reliability profile including completion, on-time, cancellation, and delay indicators.",
                data: [item],
                columns: RELIABILITY_COLUMNS,
              })
            }
          />
        </Grid>
      </Grid>
    ),
    [chartColors.error, openDrillDown],
  );

  return (
    <>
      <DashboardReport
        title="Service Reliability & Exceptions"
        description="Cancellation, late-delivery, and completion risk signals to reduce preventable service disruption."
        dataFetchFn={dataFetchFn}
        filters={filters}
        metricsConfig={metricsConfig}
        chartsConfig={chartsConfig}
      />
      <DrillDownDrawer
        open={drillDown.open}
        onClose={closeDrillDown}
        title={drillDown.title}
        subtitle={drillDown.subtitle}
        description={drillDown.description}
        data={drillDown.data}
        columns={drillDown.columns}
        summary={drillDown.summary}
      />
    </>
  );
};

export const SERVICE_OPERATIONS_REPORTS = {
  "service-dashboard": ServiceOperationsDashboard,
  "timeline-analysis": SchedulingTimelineUrgency,
  "service-type-distribution": ServiceMixDeliveryMode,
  "duration-analysis": DurationEffortTravel,
  "no-show-analysis": ServiceReliabilityExceptions,
};
